import React from 'react';
import { AlertTriangle, AlertCircle, XCircle } from 'lucide-react';

interface ErrorAlertProps {
  /** Error message to display */
  message: string;
  /** Title for the error */
  title?: string;
  /** Variant of the alert */
  variant?: 'error' | 'warning' | 'info';
  /** Callback when retry button is clicked */
  onRetry?: () => void;
  /** Text for retry button */
  retryText?: string;
  /** Whether to show as full-page centered */
  fullPage?: boolean;
  /** Additional action buttons */
  actions?: React.ReactNode;
}

const variantStyles = {
  error: {
    container: 'bg-red-50 border-red-200',
    icon: XCircle,
    iconColor: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-700',
    button: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    icon: AlertTriangle,
    iconColor: 'text-yellow-500',
    title: 'text-yellow-800',
    message: 'text-yellow-700',
    button: 'bg-yellow-600 hover:bg-yellow-700',
  },
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: AlertCircle,
    iconColor: 'text-blue-500',
    title: 'text-blue-800',
    message: 'text-blue-700',
    button: 'bg-blue-600 hover:bg-blue-700',
  },
};

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  message,
  title,
  variant = 'error',
  onRetry,
  retryText = 'Retry',
  fullPage = false,
  actions,
}) => {
  const styles = variantStyles[variant];
  const IconComponent = styles.icon;

  const alertContent = (
    <div className={`border rounded-md p-6 max-w-md ${styles.container}`}>
      <div className="flex items-center justify-center mb-3">
        <IconComponent className={`h-8 w-8 ${styles.iconColor}`} />
      </div>
      {title && (
        <h3 className={`text-lg font-semibold mb-2 text-center ${styles.title}`}>
          {title}
        </h3>
      )}
      <p className={`text-sm mb-4 text-center ${styles.message}`}>{message}</p>
      {(onRetry || actions) && (
        <div className="space-y-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className={`w-full px-4 py-2 text-white rounded-md transition-colors ${styles.button}`}
            >
              {retryText}
            </button>
          )}
          {actions}
        </div>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">{alertContent}</div>
      </div>
    );
  }

  return alertContent;
};

export default ErrorAlert;
